import { filterClasses } from "@swivl-mono/helpers";

interface PolicyHintProps {
  body: string;
  linkLabel?: string;
  /** Switch the active worksheet — used by cross-reference hint links. */
  linkPolicyKey?: string;
  onNavigate?: (policyKey: string) => void;
  /** Open this URL directly (new tab) instead of switching worksheets. */
  linkHref?: string;
}

const INFO_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="size-4">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);
const WARN_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="size-4">
    <path d="M10.3 3.3 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.3a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);

/** Worksheet hint: icon tile + guidance text. */
export function PolicyHint({ body, linkLabel, linkPolicyKey, onNavigate, linkHref }: PolicyHintProps): React.ReactElement {
  const isWarning = body.startsWith("⚠️");
  const text = body.replace(/^(💡|⚠️|⚠)\s*/u, "");

  // Info hints are a bare icon beside the text. Warnings keep the filled tile, so a
  // caution still carries more weight than a cross-reference.
  const icon = isWarning ? WARN_ICON : INFO_ICON;

  return (
    <div className="py-4">
      <div className="flex items-start gap-2">
        <span
          className={filterClasses(
            "flex shrink-0 items-center justify-center",
            isWarning ? "size-8 rounded-lg bg-yellow-50 text-yellow-900" : "pt-0.5 text-grey-600"
          )}
        >
          {icon}
        </span>
        <div className={filterClasses("body-sm text-grey-700", isWarning && "pt-1")}>
          {text}
          {linkHref && linkLabel && (
            <>
              {" "}
              <a href={linkHref} target="_blank" rel="noopener noreferrer" className="font-semibold text-orange-500 hover:underline">
                {linkLabel} →
              </a>
            </>
          )}
          {!linkHref && linkPolicyKey && linkLabel && onNavigate && (
            <>
              {" "}
              <button
                type="button"
                onClick={() => onNavigate(linkPolicyKey)}
                className="cursor-pointer font-semibold text-orange-500 hover:underline"
              >
                {linkLabel} →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
